// Anexo 1 — Ficha Familiar (Fase 6). Bloques y decisiones (modelo A: ficha anclada
// al Patient del jefe de familia, miembros como filas, sin recursos FHIR nuevos) en
// `bloquesAnexo1Datos.ts`. PDF en modo `pares`: la ficha es de la familia, no del
// paciente, así que no lleva el encabezado MSP de paciente/HCU.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple, Valores } from './camposSimplesDatos';
import {
  ABREVIATURAS_FAMILIOGRAMA_ANEXO1,
  camposEvaluacionAnexo1,
  CAMPOS_DIRECCION_ANEXO1,
  CAMPOS_EMBARAZADA_ANEXO1,
  CAMPOS_ESTABLECIMIENTO_ANEXO1,
  CAMPOS_FALLECIDO_ANEXO1,
  CAMPOS_FAMILIOGRAMA_ANEXO1,
  CAMPOS_GESTION_ANEXO1,
  CAMPOS_JEFE_ANEXO1,
  CAMPOS_MIEMBRO_ANEXO1,
  CAMPOS_PROFESIONAL_ANEXO1,
  CAMPOS_TRATAMIENTO_ANEXO1,
  CAMPOS_UBICACION_ANEXO1,
  COMPONENTES_RIESGO_ANEXO1,
  estadoInicialAnexo1,
  filaVaciaAnexo1,
  MAX_EMBARAZADAS_ANEXO1,
  MAX_EVALUACIONES_ANEXO1,
  MAX_FALLECIDOS_ANEXO1,
  MAX_GESTION_ANEXO1,
  MAX_MIEMBROS_ANEXO1,
  paresDeAnexo1,
  TITULOS_ANEXO1 as T,
  type EstadoAnexo1,
} from './bloquesAnexo1Datos';

type ClaveValores = { [K in keyof EstadoAnexo1]: EstadoAnexo1[K] extends Valores ? K : never }[keyof EstadoAnexo1];
type ClaveLista = { [K in keyof EstadoAnexo1]: EstadoAnexo1[K] extends readonly Valores[] ? K : never }[keyof EstadoAnexo1];

interface FilasDinamicasProps {
  readonly prefijoId: string;
  readonly rotulo: string;
  readonly campos: readonly CampoSimple[];
  readonly filas: readonly Valores[];
  readonly max: number;
  readonly onChange: (filas: readonly Valores[]) => void;
}

/** Filas agregables/quitables hasta el tope impreso en el papel (mismo patrón que la estructura familiar de 038). */
function FilasDinamicas({ prefijoId, rotulo, campos, filas, max, onChange }: FilasDinamicasProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      {filas.map((fila, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
          <span className="text-xs font-medium text-hce-muted">{`${rotulo} ${i + 1}`}</span>
          <CamposSimples prefijoId={`${prefijoId}${i + 1}`} campos={campos} valores={fila} columnas={4} onChange={(v) => onChange(filas.map((x, j) => (j === i ? v : x)))} />
          <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => onChange(filas.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" /> {t('formularios.fAnexo1.quitar')}
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" className="w-fit" disabled={filas.length >= max} onClick={() => onChange([...filas, filaVaciaAnexo1(campos)])}>
        {`${t('formularios.fAnexo1.agregar')} ${rotulo.toLowerCase()}`}
      </Button>
      {filas.length >= max && <p className="text-xs text-hce-muted">{t('formularios.fAnexo1.topeAlcanzado', { max })}</p>}
    </div>
  );
}

export function FormularioAnexo1({ onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const { guardando, error, guardar } = useGuardado();
  const [estado, setEstado] = useState<EstadoAnexo1>(() => estadoInicialAnexo1());
  const camposEvaluacion = camposEvaluacionAnexo1();

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      onGuardado(paresDeAnexo1(estado));
    });

  function bloque(clave: ClaveValores, titulo: string, prefijo: string, lista: readonly CampoSimple[], columnas?: 2 | 3 | 4): JSX.Element {
    return (
      <Bloque titulo={titulo}>
        <CamposSimples prefijoId={`anexo1-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
      </Bloque>
    );
  }

  function lista(clave: ClaveLista, titulo: string, prefijo: string, rotulo: string, campos: readonly CampoSimple[], max: number): JSX.Element {
    return (
      <Bloque titulo={titulo}>
        <FilasDinamicas prefijoId={`anexo1-${prefijo}`} rotulo={rotulo} campos={campos} filas={estado[clave]} max={max} onChange={(f) => setEstado({ ...estado, [clave]: f })} />
      </Bloque>
    );
  }

  const setEvaluacion = (i: number, v: Valores): void => setEstado({ ...estado, evaluaciones: estado.evaluaciones.map((x, j) => (j === i ? v : x)) });
  const gruposRiesgo = [...new Set(COMPONENTES_RIESGO_ANEXO1.map((c) => c.grupo))];

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('hub.formulario.Anexo1')}</h3>
      <p className="text-xs text-hce-muted">{t('formularios.fAnexo1.nota')}</p>

      <Bloque titulo={T.establecimiento}>
        <CamposSimples prefijoId="anexo1-est" campos={CAMPOS_ESTABLECIMIENTO_ANEXO1} valores={estado.establecimiento} columnas={3} onChange={(v) => setEstado({ ...estado, establecimiento: v })} />
        <span className="text-xs font-medium text-hce-muted">{t('formularios.fAnexo1.direccionTitulo')}</span>
        <CamposSimples prefijoId="anexo1-dir" campos={CAMPOS_DIRECCION_ANEXO1} valores={estado.direccion} columnas={3} onChange={(v) => setEstado({ ...estado, direccion: v })} />
        <CamposSimples prefijoId="anexo1-jefe" campos={CAMPOS_JEFE_ANEXO1} valores={estado.jefe} columnas={4} onChange={(v) => setEstado({ ...estado, jefe: v })} />
      </Bloque>

      {lista('miembros', T.miembros, 'mie', t('formularios.fAnexo1.miembro'), CAMPOS_MIEMBRO_ANEXO1, MAX_MIEMBROS_ANEXO1)}
      {lista('embarazadas', T.embarazadas, 'emb', t('formularios.fAnexo1.embarazada'), CAMPOS_EMBARAZADA_ANEXO1, MAX_EMBARAZADAS_ANEXO1)}
      {lista('fallecidos', T.mortalidad, 'fal', t('formularios.fAnexo1.fallecido'), CAMPOS_FALLECIDO_ANEXO1, MAX_FALLECIDOS_ANEXO1)}
      {bloque('profesional', T.profesional, 'prof', CAMPOS_PROFESIONAL_ANEXO1, 3)}

      <Bloque titulo={T.familiograma}>
        <p className="text-xs text-hce-muted">{t('formularios.fAnexo1.familiogramaNota')}</p>
        <p className="text-xs text-hce-muted">{ABREVIATURAS_FAMILIOGRAMA_ANEXO1.join(' · ')}</p>
        <CamposSimples prefijoId="anexo1-fam" campos={CAMPOS_FAMILIOGRAMA_ANEXO1} valores={estado.familiograma} onChange={(v) => setEstado({ ...estado, familiograma: v })} />
      </Bloque>
      {bloque('ubicacion', T.ubicacion, 'ubi', CAMPOS_UBICACION_ANEXO1)}
      {bloque('tratamiento', T.tratamiento, 'tra', CAMPOS_TRATAMIENTO_ANEXO1)}

      <Bloque titulo={T.riesgo}>
        <p className="text-xs text-hce-muted">{t('formularios.fAnexo1.riesgoNota', { max: MAX_EVALUACIONES_ANEXO1 })}</p>
        {estado.evaluaciones.map((ev, i) => (
          <div key={i} className="flex flex-col gap-3 rounded-md border border-border p-3">
            <span className="text-xs font-medium text-hce-muted">{`${t('formularios.fAnexo1.evaluacion')} ${i + 1}`}</span>
            <CamposSimples prefijoId={`anexo1-ev${i + 1}-f`} campos={camposEvaluacion.slice(0, 1)} valores={ev} columnas={4} onChange={(v) => setEvaluacion(i, v)} />
            {gruposRiesgo.map((grupo) => (
              <div key={grupo} className="flex flex-col gap-2">
                <span className="text-xs font-medium text-hce-ink">{grupo}</span>
                {COMPONENTES_RIESGO_ANEXO1.filter((c) => c.grupo === grupo).map((c) => (
                  <div key={c.numero} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                    <Label htmlFor={`anexo1-ev${i + 1}-d${c.numero}`} className="font-normal">{`${c.numero}. ${c.etiqueta}`}</Label>
                    {c.soloDetalle ? (
                      <span />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Checkbox id={`anexo1-ev${i + 1}-m${c.numero}`} checked={ev[`m${c.numero}`] === 'true'} onCheckedChange={(v) => setEvaluacion(i, { ...ev, [`m${c.numero}`]: v === true ? 'true' : '' })} />
                        <Label htmlFor={`anexo1-ev${i + 1}-m${c.numero}`} className="font-normal">
                          {t('formularios.fAnexo1.marcar')}
                        </Label>
                      </div>
                    )}
                    <CampoDictado
                      id={`anexo1-ev${i + 1}-d${c.numero}`}
                      etiqueta=""
                      placeholder={t('formularios.fAnexo1.detalle')}
                      valor={ev[`d${c.numero}`] ?? ''}
                      onChange={(v) => setEvaluacion(i, { ...ev, [`d${c.numero}`]: v })}
                    />
                  </div>
                ))}
              </div>
            ))}
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => setEstado({ ...estado, evaluaciones: estado.evaluaciones.filter((_, j) => j !== i) })}>
              <Trash2 className="size-4" /> {t('formularios.fAnexo1.quitar')}
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          className="w-fit"
          disabled={estado.evaluaciones.length >= MAX_EVALUACIONES_ANEXO1}
          onClick={() => setEstado({ ...estado, evaluaciones: [...estado.evaluaciones, filaVaciaAnexo1(camposEvaluacion)] })}
        >
          {t('formularios.fAnexo1.agregarEvaluacion')}
        </Button>
      </Bloque>

      {lista('gestion', T.gestion, 'ges', t('formularios.fAnexo1.filaGestion'), CAMPOS_GESTION_ANEXO1, MAX_GESTION_ANEXO1)}

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
